import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, DollarSign, Gauge, LifeBuoy, ArrowRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { api } from "@/lib/api";
import { toast } from "sonner";

function Metric({ icon: Icon, label, value, sub, bar }) {
  return (
    <div className="rounded-xl border border-brand-line bg-white p-4 shadow-soft" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-slate-500">
        <Icon size={15} className="text-brand" strokeWidth={1.8} />
        <p className="text-sm">{label}</p>
      </div>
      <p className="mt-1 font-heading text-2xl font-semibold">{value}</p>
      {bar != null ? (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-surface">
          <div className="h-full rounded-full bg-brand" style={{ width: `${bar}%` }} />
        </div>
      ) : (
        <p className="mt-1 text-xs text-slate-400">{sub}</p>
      )}
    </div>
  );
}

export default function FounderDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null);

  const load = useCallback(() => {
    api.get("/founder/dashboard").then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const intervene = async (student_id, action) => {
    setActing(student_id);
    try {
      await api.post("/founder/intervene", { student_id, kind: action.includes("Review") ? "review" : "check_in" });
      toast.success("Logged. The student sees your support on their side.");
      load();
    } catch {
      toast.error("Could not record that. Try again.");
    } finally {
      setActing(null);
    }
  };

  if (!data) return <FullPageLoader label="Loading founder view…" />;
  if (data.error) return <FullPageLoader label="Loading…" />;
  const m = data.metrics;

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-5xl px-8 py-10 ltifade" data-testid="founder-dashboard">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Founder dashboard</h1>
        <p className="mt-1 text-slate-500">Outcomes first. {m.needs_support} {m.needs_support === 1 ? "student" : "students"} could use your help today.</p>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric icon={Rocket} label="Launched" value={m.launched} sub="businesses live" />
          <Metric icon={DollarSign} label="First sales" value={m.first_sales} sub="made their first sale" />
          <Metric icon={Gauge} label="Avg readiness" value={`${m.avg_readiness}%`} bar={m.avg_readiness} />
          <Metric icon={LifeBuoy} label="Needs support" value={m.needs_support} sub="queue below" />
        </div>

        <div className="mt-6 rounded-xl border-2 border-brand/20 bg-white p-5 shadow-soft" data-testid="attention-queue">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">Needs support · sorted by days stalled</p>
          {data.queue.length === 0 ? (
            <p className="mt-4 text-sm text-slate-500">Everyone's moving. Nothing needs your attention right now.</p>
          ) : (
            <div className="mt-3 divide-y divide-brand-line">
              {data.queue.map((s) => (
                <div key={s.student_id} className="flex items-center gap-3 py-3" data-testid={`queue-item-${s.student_id}`}>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-surface text-xs font-semibold text-brand-text">{s.initials}</span>
                  <button onClick={() => navigate(`/founder/students/${s.student_id}`)} className="flex-1 text-left">
                    <p className="text-sm font-medium hover:underline">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.reason}</p>
                  </button>
                  <button
                    data-testid={`intervene-btn-${s.student_id}`}
                    onClick={() => intervene(s.student_id, s.action)}
                    disabled={acting === s.student_id}
                    className="rounded-lg border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-text transition-all duration-200 hover:bg-brand-surface disabled:opacity-50"
                  >
                    {s.action}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-slate-500 shadow-soft">
          <span>Milestone completion <span className="font-medium text-slate-700">{data.milestone_completion}%</span> across {data.student_count} students</span>
          <button onClick={() => navigate("/founder/students")} className="inline-flex items-center gap-1.5 font-medium text-brand-text hover:underline">
            View all students <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
